/**
 * Measurement display card for Settings > My-Kitchen.
 *
 * One kitchen-wide choice for how recipe amounts read on every chef
 * surface (order details, the recipe drawer, printed recipe packs):
 *
 *   - Standard  — weights become the volumes a cook measures (via the
 *                 ingredient's density), with kitchen shorthand.
 *   - Technical — exactly as the menus team entered it: authored unit,
 *                 no conversion, plain decimals.
 *
 * Saves on change to `PATCH /api/kitchen-settings/:orgId/measurement-
 * preference` (CHEF_ADMIN only; staff see the current value read-only).
 */

import { Card, CardContent, CardHeader, CardTitle } from '@ordo/ui/card'
import { Label } from '@ordo/ui/label'
import { RadioGroup, RadioGroupItem } from '@ordo/ui/radio-group'
import { Skeleton } from '@ordo/ui/skeleton'
import { toast } from '@ordo/ui/sonner'
import { Ruler } from 'lucide-react'

import {
  type MeasurementDisplayMode,
  useKitchenMeasurementPreferenceQuery,
  useUpdateKitchenMeasurementPreferenceMutation,
} from '@/api/kitchen-settings'

interface ModeOption {
  value: MeasurementDisplayMode
  label: string
  description: string
  example: string
}

export const MEASUREMENT_DISPLAY_OPTIONS: ReadonlyArray<ModeOption> = [
  {
    value: 'STANDARD',
    label: 'Standard',
    description: 'Weights become the volumes a cook measures, with kitchen shorthand.',
    example: '1 cup + 4 1/2 tbsp',
  },
  {
    value: 'TECHNICAL',
    label: 'Technical',
    description: 'Exactly as the menus team entered it. Authored unit, no conversion, plain decimals.',
    example: '10.9 oz',
  },
]

export interface MeasurementDisplayCardProps {
  kitchenOrgId: string
  isAdmin: boolean
}

export function MeasurementDisplayCard({ kitchenOrgId, isAdmin }: MeasurementDisplayCardProps) {
  const query = useKitchenMeasurementPreferenceQuery(kitchenOrgId)
  const mutation = useUpdateKitchenMeasurementPreferenceMutation()
  const current = query.data?.measurementPreference.displayMode ?? 'STANDARD'
  const disabled = !isAdmin || mutation.isPending

  function onChange(next: string) {
    const displayMode = next as MeasurementDisplayMode
    if (displayMode === current) {
      return
    }
    mutation.mutate(
      { organizationId: kitchenOrgId, displayMode },
      {
        onError: (err) => {
          const message = err instanceof Error ? err.message : 'Unknown error'
          toast.error("Couldn't save measurement display", { description: message })
        },
      },
    )
  }

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-base">
          <Ruler className="h-4 w-4" />
          Measurement display
        </CardTitle>
        <p className="text-sm text-muted-foreground">
          How recipe amounts read everywhere in this kitchen: order details, the recipe drawer, and printed recipe
          packs.
        </p>
      </CardHeader>
      <CardContent>
        {query.isPending && !query.data ? (
          <div className="flex flex-col gap-2">
            <Skeleton className="h-16 w-full" />
            <Skeleton className="h-16 w-full" />
          </div>
        ) : (
          <RadioGroup value={current} onValueChange={onChange} disabled={disabled} className="flex flex-col gap-2">
            {MEASUREMENT_DISPLAY_OPTIONS.map((opt) => (
              <Label
                key={opt.value}
                htmlFor={`measurement-display-${opt.value}`}
                className="flex cursor-pointer items-start gap-3 rounded-md border bg-card p-3 hover:bg-accent/50">
                <RadioGroupItem id={`measurement-display-${opt.value}`} value={opt.value} className="mt-0.5" />
                <div className="flex min-w-0 flex-1 flex-col gap-0.5">
                  <span className="text-sm font-medium">{opt.label}</span>
                  <span className="text-xs text-muted-foreground">{opt.description}</span>
                </div>
                <span className="shrink-0 font-mono text-xs text-muted-foreground">{opt.example}</span>
              </Label>
            ))}
          </RadioGroup>
        )}
        {!isAdmin ? (
          <p className="pt-3 text-xs text-muted-foreground">Only a kitchen admin can change this setting.</p>
        ) : null}
      </CardContent>
    </Card>
  )
}
